import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';

// --- Log types (sls-logger) ---
export type LogLevel =
    | 'SUCCESS'
    | 'INFO'
    | 'WARNING'
    | 'ERROR'
    | 'DEBUG'
    | 'CRITICAL'
    | 'VISIT';

export interface LogEntry {
    id: string;
    level: LogLevel;
    message: string;
    details?: string | null;
    source: string;
    action?: string | null;
    timestamp?: string | null;
    systemUserId: string;
    systemId: string;
    catalogItemId?: string | null;
    catalogItemTypeId?: string | null;
    sessionId?: string | null;
    requestId?: string | null;
    ipAddress?: string | null;
    userAgent?: string | null;
    executionTime?: number | null;
    memoryUsage?: number | null;
    errorCode?: string | null;
    stackTrace?: string | null;
    businessUnit?: string | null;
    environment?: string | null;
    tags?: string[] | null;
    metadata?: unknown | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export interface CreateLogDto {
    systemId: string;
    systemUserId: string;
    level: LogLevel;
    message: string;
    source: string;
    timestamp?: string;
    details?: string;
    action?: string;
    catalogItemId?: string;
    catalogItemTypeId?: string;
    sessionId?: string;
    requestId?: string;
    ipAddress?: string;
    userAgent?: string;
    executionTime?: number;
    memoryUsage?: number;
    errorCode?: string;
    stackTrace?: string;
    businessUnit?: string;
    environment?: string;
    tags?: string[];
    metadata?: unknown;
}

export type IngestLogDto = Omit<CreateLogDto, 'systemId' | 'timestamp'> & {
    systemUserId: string;
};

export interface ListLogsQuery {
    systemId?: string;
    systemUserId?: string;
    level?: LogLevel;
    source?: string;
    catalogItemId?: string;
    sessionId?: string;
    requestId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    nextToken?: string;
}

export interface ListLogsResponse {
    items: LogEntry[];
    nextToken?: string;
}

export type AggregationsGroupBy = 'level' | 'source' | 'date';

export interface LogsAggregationsQuery {
    dateFrom?: string;
    dateTo?: string;
    systemId?: string;
    groupBy?: AggregationsGroupBy;
}

export type LogsAggregationsResponse = Record<string, number>;

// --- Config & service ---
export interface SlsLoggerConfig {
    baseUrl: string;
    stage?: string;
    apiKey?: string;
}

export const SLS_LOGGER_CONFIG = new InjectionToken<SlsLoggerConfig>(
    'SLS_LOGGER_CONFIG'
);

@Injectable({ providedIn: 'root' })
export class SlsLoggerService {
    constructor(
        private readonly http: HttpClient,
        @Optional()
        @Inject(SLS_LOGGER_CONFIG)
        private readonly config?: SlsLoggerConfig
    ) {}

    health(): Observable<{ ok: boolean }> {
        const url = this.url('/health');
        return this.http
            .get<{ ok: boolean }>(url)
            .pipe(catchError(() => of({ ok: false } as any)));
    }

    listLogs(
        query: ListLogsQuery = {},
        _apiKey?: string
    ): Observable<ListLogsResponse> {
        // TODO: restore GET /logs when the API response no longer crashes the app
        console.warn(
            '[SlsLoggerService] listLogs disabled — TODO: re-enable HTTP call',
            query
        );
        return of({ items: [] });

        // const url = this.url('/logs');
        // const headers = this.apiKeyHeaders(apiKey);
        // let params = new HttpParams();
        // Object.entries(query).forEach(([k, v]) => {
        //     if (v !== undefined && v !== null && v !== '') {
        //         params = params.set(k, String(v));
        //     }
        // });
        // return this.http.get<ListLogsResponse>(url, { headers, params });
    }

    getLog(id: string, apiKey?: string): Observable<LogEntry> {
        const url = this.url(`/logs/${encodeURIComponent(id)}`);
        const headers = this.apiKeyHeaders(apiKey);
        return this.http.get<LogEntry>(url, { headers });
    }

    getAggregations(
        query: LogsAggregationsQuery = {},
        apiKey?: string
    ): Observable<LogsAggregationsResponse> {
        const url = this.url('/logs/aggregations');
        const headers = this.apiKeyHeaders(apiKey);
        let params = new HttpParams();
        Object.entries(query).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') {
                params = params.set(k, String(v));
            }
        });
        return this.http.get<LogsAggregationsResponse>(url, {
            headers,
            params
        });
    }

    ingest(body: IngestLogDto, apiKey?: string): Observable<LogEntry> {
        const url = this.url('/ingest');
        const headers = this.apiKeyHeaders(apiKey);
        return this.http.post<LogEntry>(url, body, { headers });
    }

    webhooksLogs(
        payload: IngestLogDto | IngestLogDto[] | { logs: IngestLogDto[] },
        apiKey?: string
    ): Observable<LogEntry | { created: number; ids: string[] }> {
        const url = this.url('/webhooks/logs');
        const headers = this.apiKeyHeaders(apiKey);
        return this.http.post<LogEntry | { created: number; ids: string[] }>(
            url,
            payload,
            { headers }
        );
    }

    private url(path: string): string {
        if (!this.config?.baseUrl) {
            throw new Error('SLS Logger not configured: baseUrl is required');
        }
        const base = this.config.baseUrl.replace(/\/+$/, '');
        const p = path.startsWith('/') ? path : `/${path}`;
        return `${base}${p}`;
    }

    private apiKeyHeaders(apiKeyOverride?: string): HttpHeaders {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const key = apiKeyOverride ?? this.config?.apiKey;
        if (key) headers = headers.set('X-Api-Key', key);
        return headers;
    }
}
